import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useState } from "react";
import { useParams } from "react-router";
import { Abweisung } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import type { Zeile } from "../plattform/lesemodelle";
import { anrufen, navigieren } from "../plattform/position";
import { personAus, positionAus, useDrueckjagdSammlung, type Rahmen } from "./daten";
import { NOTFALLSYMBOL } from "./eingehend";

const zeit = (z: Zeile) => (z["Zeitpunkt"] instanceof Date ? z["Zeitpunkt"].getTime() : 0);

/**
 * Ansicht „Notrufe“ (oberflaeche.md §6 V-19, `main` „Notrufe“; design/README §9.9, Referenz `#s-notrufe`): geöffnet
 * über den Zähler „Notruf“ des Lagebilds wie „Offene Suchen“ und „Kontingentstand“. Oben das Notrufband mit dem neuesten
 * Notruf als Zusammenfassung ohne Bedienelement (Notfallart, Person, Stand; K70, K85 a), darunter je offenem Notruf ein
 * `article` „<Notfallart>“ in der kompakten Eintragsform mit „Anrufen“, „Navigation“, „Erledigt“, „Quittieren“ (Bereich
 * wie die Referenz), dazu „Zurück“ im kompakten Zustandsband. Als eigene Ansicht steht auch auf kleinen Geräten ein
 * ganzer Notruf im Bild (im Lagebild blieb dafür unter Zählern, Notrufband und Auslöser kein Platz).
 */
export function Notrufe({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const notrufe = [...useDrueckjagdSammlung(id, "offene_notrufe")].sort((a, b) => zeit(a) - zeit(b));
  const notruf112 = useDrueckjagdSammlung(id, "notfallkontakte").find((k) => k["Art"] === "Notruf");
  const [abgewiesen, setAbgewiesen] = useState<string | null>(null);
  const notrufBefehl = async (name: "NotrufQuittieren" | "NotrufErledigen", notruf: string) => {
    const a = await befehl(name, { Notruf: notruf });
    setAbgewiesen(a.netzfehler ? null : abweisungsgrund(a));
  };
  const neuester = notrufe[notrufe.length - 1];
  return rahmen({
    drueckjagdId: id, name: "Notrufe",
    oben: neuester ? <Zusammenfassung notruf={neuester} /> : undefined,
    children: (
      <section className="liste-bereich" role="region" aria-label="Notrufe">
        <Abweisung grund={abgewiesen} />
        {notrufe.map((n) => (
          <Notruf key={n._id} notruf={n} onQuittieren={() => void notrufBefehl("NotrufQuittieren", n._id)}
            onErledigt={() => void notrufBefehl("NotrufErledigen", n._id)} onAnrufen={() => anrufen(notruf112?.["Anruf"] as string | undefined)} />
        ))}
        {notrufe.length === 0 ? <p className="leer platte"><Symbol name="haken" /><span>—</span></p> : null}
      </section>
    ),
  });
}

/** Neuester Notruf über der Liste (Referenz `liste__notrufband`, `data-notrufband`): Notfallart, Person · Stand. */
function Zusammenfassung({ notruf }: { notruf: Zeile }) {
  const art = String(notruf["Notfallart"]);
  const unter = [personAus(notruf["Melder"])?.Name, notruf["Stand"] ? `Stand ${String(notruf["Stand"])}` : null].filter(Boolean).join(" · ");
  return (
    <p className="liste__lage liste__notrufband platte" role="status" data-notrufband="">
      <Symbol name={NOTFALLSYMBOL[art] ?? "notruf"} /><b>{art}</b><span>{unter}</span>
    </p>
  );
}

/**
 * Offener Notruf in der kompakten Eintragsform der Referenz (`eintraege_mit`, `#s-notrufe`): Symbol der Notfallart,
 * Titel, eine Zeile „Stand 7a · 10:17“ (ohne Stand der Hund bzw. die Person), darunter die vier Knöpfe. Ob
 * „Quittieren“ noch wirkt, entscheidet der Server (ADR-0010; ein schon quittierter Notruf wird abgewiesen).
 */
function Notruf({ notruf, onQuittieren, onErledigt, onAnrufen }: { notruf: Zeile; onQuittieren: () => void; onErledigt: () => void; onAnrufen: () => void }) {
  const art = String(notruf["Notfallart"]);
  const titelId = `notruf-${notruf._id}`;
  const position = positionAus(notruf["Position"]);
  const ort = notruf["Stand"] ? `Stand ${String(notruf["Stand"])}` : notruf["Hund"] ? String(notruf["Hund"]) : personAus(notruf["Melder"])?.Name;
  const meta = [ort, notruf["Zeitpunkt"] instanceof Date ? berlinUhrzeit(notruf["Zeitpunkt"]) : null].filter(Boolean).join(" · ");
  return (
    <article className="eintrag eintrag--knoepfe platte" aria-labelledby={titelId}>
      <Symbol name={NOTFALLSYMBOL[art] ?? "notruf"} />
      <h3 className="eintrag__t" id={titelId}>{art}</h3>
      <span className="eintrag__meta">{meta}</span>
      {notruf["Bemerkung"] ? <span className="eintrag__meta">{String(notruf["Bemerkung"])}</span> : null}
      <div className="raster raster--knoepfe">
        <Kachel symbol="telefon" begriff="Anrufen" art="kompakt" onPress={onAnrufen} />
        <Kachel symbol="navigation" begriff="Navigation" art="kompakt" isDisabled={!position} onPress={() => navigieren(position)} />
        <Kachel symbol="haken" begriff="Erledigt" art="kompakt" onPress={onErledigt} />
        <Kachel symbol="quittieren" begriff="Quittieren" art="kompakt" onPress={onQuittieren} />
      </div>
    </article>
  );
}
